import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { formatClock, formatDuration } from '../lib/format'
import { parseTags } from '../data/tags'
import { matchCategories } from '../data/taxonomy'
import type { Block, Category } from '../data/types'

export interface QuickPatch {
  categoryId?: string
  note?: string | null
  tags?: string[]
}

interface Props {
  block: Block
  /** Where in the block the menu was opened — the split point. */
  at: number
  palette: Category[]
  knownTags: string[]
  x: number
  y: number
  onPatch: (patch: QuickPatch) => void
  onEdit: () => void
  onSplit: (at: number) => void
  onDelete: () => void
  onClose: () => void
}

const EDGE = 8

/**
 * Right-click on a block: change what it is without opening the dialog. The
 * fields that fit a menu are here; the ones that need validation — the times —
 * are one click away in the editor.
 */
export function BlockMenu({
  block,
  at,
  palette,
  knownTags,
  x,
  y,
  onPatch,
  onEdit,
  onSplit,
  onDelete,
  onClose,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ left: x, top: y, ready: false })
  const [note, setNote] = useState(block.note ?? '')
  const [tags, setTags] = useState(block.tags.join(', '))
  const [query, setQuery] = useState('')
  /**
   * The row Enter would pick, or null when the keyboard has not claimed one
   * yet. Null rather than 0 so a stray Enter on a menu nobody has typed into
   * cannot silently recategorise the block.
   */
  const [active, setActive] = useState<number | null>(null)

  const matches = useMemo(() => matchCategories(palette, query), [palette, query])

  // Placed after measuring, so a menu opened near an edge folds back on screen
  // instead of hanging off it.
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const { width, height } = node.getBoundingClientRect()
    setPosition({
      left: Math.max(EDGE, Math.min(x, window.innerWidth - width - EDGE)),
      top: Math.max(EDGE, Math.min(y, window.innerHeight - height - EDGE)),
      ready: true,
    })
  }, [x, y])

  /**
   * Typing is the fast path, so with a mouse the menu opens ready for it.
   *
   * Only once it is on screen: the menu is hidden while it measures itself, and
   * a hidden element cannot take focus. And only with a mouse: a phone opens
   * this with a long press and would answer the focus by raising a keyboard
   * over the very list the box filters.
   */
  useEffect(() => {
    if (!position.ready || !window.matchMedia('(pointer: fine)').matches) return
    searchRef.current?.focus({ preventScroll: true })
  }, [position.ready])

  // Keep the row Enter would pick in sight when the arrows walk past the fold.
  useEffect(() => {
    if (active === null) return
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [active])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // A filter is the first thing Escape takes back, so a search that found
      // nothing does not also cost you the menu.
      if (query) setQuery('')
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, query])

  /** Text fields commit on the way out, whichever way out that is. */
  function flush() {
    const patch: QuickPatch = {}
    const nextNote = note.trim() || null
    if (nextNote !== block.note) patch.note = nextNote
    const nextTags = parseTags(tags)
    if (nextTags.join(',') !== block.tags.join(',')) patch.tags = nextTags
    if (Object.keys(patch).length > 0) onPatch(patch)
  }

  function close() {
    flush()
    onClose()
  }

  function choose(category: Category) {
    flush()
    if (category.id !== block.categoryId) onPatch({ categoryId: category.id })
    onClose()
  }

  /** Arrows walk the matches; Enter takes the one they are on. */
  function onSearchKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (matches.length === 0) return
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((current) =>
        current === null ? 0 : Math.min(matches.length - 1, Math.max(0, current + step)),
      )
      return
    }
    if (event.key === 'Enter' && active !== null) {
      event.preventDefault()
      const category = matches[active]
      if (category) choose(category)
    }
  }

  const splittable = at - block.start >= 60_000 && (block.end ?? Number.POSITIVE_INFINITY) - at >= 60_000

  return (
    <>
      <div className="fixed inset-0 z-40" onPointerDown={close} onContextMenu={(event) => {
        event.preventDefault()
        close()
      }} />
      <div
        ref={ref}
        role="menu"
        aria-label="Block actions"
        style={{ left: position.left, top: position.top, visibility: position.ready ? 'visible' : 'hidden' }}
        className="fixed z-50 flex max-h-[min(560px,80vh)] w-56 flex-col overflow-hidden rounded-xl border border-line-strong bg-surface py-1 shadow-2xl"
      >
        <p className="shrink-0 px-3 pt-1 pb-2 text-[12px] leading-tight text-faint tnum">
          {formatClock(new Date(block.start))}
          {block.end === null
            ? ' · running'
            : `–${formatClock(new Date(block.end))} · ${formatDuration(block.end - block.start)}`}
        </p>

        <div className="shrink-0 px-2 pb-2">
          <input
            ref={searchRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              // Typing aims at something, so the first match is the one Enter
              // takes; clearing the box gives up that claim again.
              setActive(event.target.value.trim() ? 0 : null)
            }}
            onKeyDown={onSearchKey}
            placeholder="Search categories"
            aria-label="Search categories"
            className="w-full rounded-md border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong"
          />
        </div>

        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
          {matches.map((category, index) => (
            <button
              key={category.id}
              type="button"
              role="menuitemradio"
              aria-checked={category.id === block.categoryId}
              onPointerEnter={() => setActive(index)}
              onClick={() => choose(category)}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] transition-colors duration-150 ${
                index === active ? 'bg-raised' : ''
              }`}
            >
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: category.color }}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate text-text">{category.label}</span>
              {category.id === block.categoryId ? (
                <span aria-hidden="true" className="shrink-0 text-[12px] text-accent">
                  ✓
                </span>
              ) : null}
            </button>
          ))}
          {matches.length === 0 ? (
            <p className="px-3 py-2 text-[12px] text-faint">Nothing called that.</p>
          ) : null}
        </div>

        <div className="mt-1 shrink-0 border-t border-line px-3 py-2">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') close()
            }}
            placeholder="Note"
            aria-label="Note"
            className="w-full rounded-md border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong"
          />
          <input
            value={tags}
            list="menu-known-tags"
            onChange={(event) => setTags(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') close()
            }}
            placeholder="Tags"
            aria-label="Tags"
            className="mt-1.5 w-full rounded-md border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong"
          />
          <datalist id="menu-known-tags">
            {knownTags.map((known) => (
              <option key={known} value={known} />
            ))}
          </datalist>
        </div>

        <div className="shrink-0 border-t border-line pt-1">
          <MenuAction
            onClick={() => {
              flush()
              onEdit()
              onClose()
            }}
          >
            Edit details…
          </MenuAction>
          {splittable ? (
            <MenuAction
              onClick={() => {
                flush()
                onSplit(at)
                onClose()
              }}
            >
              Split at {formatClock(new Date(at))}
            </MenuAction>
          ) : null}
          <MenuAction
            danger
            onClick={() => {
              onDelete()
              onClose()
            }}
          >
            Delete
          </MenuAction>
        </div>
      </div>
    </>
  )
}

function MenuAction({
  onClick,
  danger = false,
  children,
}: {
  onClick: () => void
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`block w-full px-3 py-1.5 text-left text-[13px] transition-colors duration-150 hover:bg-raised ${
        danger ? 'text-danger' : 'text-dim hover:text-text'
      }`}
    >
      {children}
    </button>
  )
}
